import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import VueLazyLoad from "vue-lazyload";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "./plugins/base";
import "./plugins/chartist";
import "./plugins/vee-validate";
import "./plugins/moment";
import vuetify from "./plugins/vuetify";
import i18n from "./i18n";
import VueApexCharts from "vue-apexcharts";
import FlipCountdown from "vue2-flip-countdown";
import VuePdfApp from "vue-pdf-app";
import ErrorFixes from "@/utils/error-fixes";
import VersionPlugin from "./plugins/version-plugin"; // เพิ่มบรรทัดนี้

// dev (npm run serve / localhost:8080) ใช้ API เครื่อง local เสมอ ไม่ต้องสลับ comment มือ —
// เดิม hardcode ไปที่ production ตลอด ทำให้ config ฝั่ง dev (เช่น thaid_secrets.php ที่ปิด 2FA
// ไว้สำหรับเครื่องนี้) ไม่มีผลอะไรเลยเพราะ request จริงๆ วิ่งไปหา production เสมอ
const request = axios.create({
  baseURL:
    process.env.NODE_ENV === "development"
      ? "https://hrvec.ovec.go.th/HRvec_api/"
      : "https://hrvec.ovec.go.th/HRvec_api/"
});

// แนบ session_token (ออกโดย login.php/login_etc.php หลัง login สำเร็จ) ไปกับทุก request
// อัตโนมัติผ่าน header X-Session-Token — endpoint นำร่องที่แปลงมาใช้ requireSession() แล้ว
// จะอ่าน header นี้แทนการพึ่ง static ApiKey เพียงอย่างเดียว (ดู rbac_helpers.php ฝั่ง backend)
request.interceptors.request.use(config => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.session_token) {
      config.headers["X-Session-Token"] = user.session_token;
    }
  } catch (e) {
    // ไม่มี session หรือ parse ไม่ได้ — ปล่อยผ่านไม่แนบ header (endpoint จะตอบ 401 เอง)
  }
  return config;
});

// จับ 401 (session ไม่มี/หมดอายุ) จากทุก endpoint ที่ผ่าน requireSession()/requireSuperAdmin()
// แบบรวมศูนย์จุดเดียว — เดิมแต่ละหน้าต้อง try/catch เองทีละจุด พลาดง่ายและเพิ่งเจอปัญหาซ้ำ
// มาแล้ว 2 ครั้ง (session เก่าที่ login ไว้ก่อนเพิ่มระบบ session token) จึงย้ายมาจับที่นี่ทีเดียว
// หมายเหตุ: ไม่จับ 403 เพราะ 403 แปลว่า session ยังถูกต้องแค่ role ไม่พอ (บังคับ logout ไม่ช่วย)
// ปล่อยให้แต่ละหน้าจัดการข้อความ "ไม่มีสิทธิ์" ของตัวเอง
let sessionExpiredHandled = false;
request.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401 && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      localStorage.removeItem("user");
      if (router.currentRoute.path !== "/pages/login") {
        alert("Session หมดอายุ หรือยังไม่ได้เข้าสู่ระบบ กรุณาเข้าสู่ระบบใหม่อีกครั้ง");
        router.push("/pages/login").catch(() => {});
      }
      // ตั้งเวลาให้จับ 401 ซ้ำได้อีกครั้งหลัง redirect เสร็จ (กัน request ที่ค้างอยู่พร้อมกันหลายตัว
      // เด้ง alert ซ้ำๆ ตอนเปลี่ยนหน้าเดียวกัน)
      setTimeout(() => {
        sessionExpiredHandled = false;
      }, 3000);
    }
    // ทำเครื่องหมายไว้ให้ Vue.config.errorHandler รู้ว่าเคสนี้จัดการ/แจ้งผู้ใช้ไปแล้ว ไม่ต้อง log ซ้ำ
    if (error.response && error.response.status === 401) {
      error.isSessionExpired = true;
    }
    return Promise.reject(error);
  }
);

Vue.config.productionTip = false;

// ================= Vue Plugins =================
Vue.use(VueAxios, request);
Vue.use(VueLazyLoad);
Vue.use(VueApexCharts);
Vue.use(FlipCountdown);
Vue.use(VersionPlugin); // เพิ่มบรรทัดนี้

Vue.prototype.$errorFixes = ErrorFixes;

Vue.component("vue-pdf-app", VuePdfApp);
Vue.component("apexchart", VueApexCharts);

Vue.config.errorHandler = (err, vm, info) => {
  // 401 ที่ response interceptor ด้านบนจัดการ (แจ้งผู้ใช้ + redirect ไป login) แล้ว ไม่ต้อง log ซ้ำ
  if (err && err.isSessionExpired) return;
  console.error("Vue Error:", err);
  console.error("Component:", vm?.$options.name);
  console.error("Info:", info);
};

// ================= Create Vue Instance =================
const app = new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: h => h(App),
  
  // โหลด version ตอนเริ่มต้น
  async created() {
    await this.$loadAppVersion();
  }
}).$mount("#app");